/**
 * 충전기 단위 가동률 분포 — "평균 99%" 뒤에 무엇이 있나.
 *
 * **히스토그램 하나를 그리지 않는 이유.** 실측에서 96.8% 의 충전기가 정확히
 * 1.0 이다. 선형 막대로 그리면 막대 하나가 화면을 채우고 나머지 19개 구간은
 * 픽셀 한 줄도 못 얻는다 — 정작 봐야 할 것이 안 보인다.
 *
 * 그래서 지배값을 **숫자로 먼저 말하고**(타일 3개), 차트는 나머지 꼬리만
 * 그린다. 꼬리 안에서도 개수가 10 에서 2천까지 벌어지므로 세로축을 로그로
 * 둔다 — 축 라벨과 각주에 그렇게 썼고, 정확한 개수는 표 보기에 있다.
 */

import { useQuery } from '@tanstack/react-query'
import type { EChartsOption } from 'echarts'
import { useMemo } from 'react'

import { ChartFrame } from '@/components/ChartFrame'
import { EChart } from '@/components/EChart'
import { fetchEnvelope } from '@/lib/api'
import { axisStyle, CHART_H } from '@/lib/chart'
import { cssVar } from '@/lib/cssvar'
import { int, pct } from '@/lib/fmt'
import type { DistributionBin, DistributionData, Envelope } from '@/lib/types'
import { filterKey, toQueryFilters, useFilters } from '@/stores/filters'

interface Split {
  perfect: number
  dead: number
  bins: DistributionBin[]
  partial: number
  total: number
}

export function AvailabilityDistribution() {
  const filters = useFilters()
  const q = useQuery({
    queryKey: ['metrics/distribution', filterKey(filters)],
    queryFn: ({ signal }) =>
      fetchEnvelope<DistributionData>('/metrics/distribution', toQueryFilters(filters), {}, signal),
    staleTime: 5 * 60 * 1000,
  })

  const env = q.data as Envelope<DistributionData> | undefined
  // 지배값(1.0)·완전 정지(0.0)·5% 구간은 서버가 전수로 센다 (queries.sql metrics_distribution).
  // 배포 직후 브라우저 캐시(max-age 300초)가 옛 모양(values[])을 줄 수 있다 — 칸이 없으면 빈 상태로 둔다
  const s = useMemo<Split>(() => {
    const d = env?.data
    if (!d || !Array.isArray(d.bins)) return { perfect: 0, dead: 0, partial: 0, bins: [], total: 0 }
    return { perfect: d.perfect, dead: d.dead, partial: d.partial, bins: d.bins, total: d.n }
  }, [env])

  const option = useMemo<EChartsOption>(() => {
    const warn = cssVar('--c-warn', '#8a6a12')
    const ink3 = cssVar('--c-ink-3', '#6e6e73')
    const hairline = cssVar('--c-hairline', '#e0e0e0')

    return {
      // top 은 세로축 이름("로그 눈금") 자리다 — 좁히면 이름이 잘려 로그축이
      // 선형축처럼 읽힌다
      grid: { left: 52, right: 12, top: 30, bottom: 40 },
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          const p = Array.isArray(params) ? params[0] : params
          const b = s.bins[(p as { dataIndex: number }).dataIndex]
          if (!b) return ''
          return `가동률 ${pct(b.from, 0)} ~ ${pct(b.to, 0)}<br/><b>${int(b.n)}기</b>`
        },
      },
      xAxis: {
        type: 'category',
        data: s.bins.map((b) => pct(b.from, 0)),
        name: '가동률 (1.0 과 0.0 제외)',
        nameLocation: 'middle',
        nameGap: 24,
        nameTextStyle: { fontSize: 10, color: ink3 },
        ...axisStyle(ink3, hairline),
        axisLabel: { fontSize: 10, color: ink3, interval: 3 },
      },
      yAxis: {
        type: 'log',
        min: 1,
        name: '충전기 수 (로그 눈금)',
        nameTextStyle: { fontSize: 10, color: ink3, align: 'left' },
        ...axisStyle(ink3, hairline),
      },
      series: [
        {
          type: 'bar',
          data: s.bins.map((b) => Math.max(b.n, 0.9)), // 로그축은 0 을 그릴 수 없다
          // 막대 사이 2px 표면 간격 · 데이터 끝만 둥글게
          barCategoryGap: '18%',
          itemStyle: { color: warn, borderRadius: [4, 4, 0, 0] },
        },
      ],
    }
  }, [s])

  const table = (
    <div className="ctable">
      <table className="data-table">
        <caption className="sr-only">충전기 단위 가동률 분포</caption>
        <thead>
          <tr>
            <th scope="col">구간</th>
            <th scope="col" className="ta-r">
              충전기
            </th>
            <th scope="col" className="ta-r">
              비율
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row">정확히 0% (관측 내내 DOWN)</th>
            <td className="num ta-r">{int(s.dead)}</td>
            <td className="num ta-r">{pct(s.total ? s.dead / s.total : null)}</td>
          </tr>
          {s.bins.map((b) => (
            <tr key={b.from}>
              <th scope="row">
                {pct(b.from, 0)} ~ {pct(b.to, 0)}
              </th>
              <td className="num ta-r">{int(b.n)}</td>
              <td className="num ta-r">{pct(s.total ? b.n / s.total : null)}</td>
            </tr>
          ))}
          <tr>
            <th scope="row">정확히 100% (관측 내내 UP)</th>
            <td className="num ta-r">{int(s.perfect)}</td>
            <td className="num ta-r">{pct(s.total ? s.perfect / s.total : null)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  )

  return (
    <ChartFrame
      title="충전기별 가동률 분포"
      question="충전기 한 대씩 보면 어떤가 — 전체 가동률 99% 는 '모두가 99%' 라는 뜻이 아니다"
      meta={env?.meta}
      reading="충전기 한 대를 한 번씩 세어 가동률별로 쌓았습니다. 위 타일 세 개가 전체 그림이고, 아래 막대는 그중 '한 번이라도 DOWN' 이었던 충전기만 자세히 나눈 것입니다."
      encoding={[
        {
          role: '가로축',
          what: '가동률 5%p 구간 (0% ~ 100%)',
          detail:
            '라벨은 각 구간의 시작값입니다. 예를 들어 "80%" 막대는 가동률 80% 이상 85% 미만을 뜻합니다. 정확히 0% 와 정확히 100% 인 충전기는 이 축에서 빼고 위 타일에 숫자로 적었습니다.',
        },
        {
          role: '세로축',
          what: '충전기 수 (로그 눈금)',
          detail:
            '구간별 개수가 열 단위에서 천 단위까지 벌어져 선형 눈금에서는 작은 구간이 픽셀 한 줄도 얻지 못합니다. 눈금 한 칸이 10배이므로 막대 높이를 눈으로 비교하지 말고 정확한 개수는 표 보기를 쓰세요.',
        },
        {
          role: '타일 3개',
          what: '관측 내내 UP · 한 번이라도 DOWN · 관측 내내 DOWN',
          detail: '서로 겹치지 않고 합이 100% 입니다. 막대는 가운데 항목만 나눈 것입니다.',
        },
      ]}
      isLoading={q.isLoading}
      error={q.error as Error | null}
      isEmpty={s.total === 0}
      onRetry={() => void q.refetch()}
      table={table}
      notes={[
        {
          label: '히스토그램을 하나로 그리지 않은 이유.',
          text: `실측에서 ${pct(s.total ? s.perfect / s.total : null, 1)} 의 충전기가 정확히 100% 입니다. 선형 막대로 그리면 막대 하나가 화면을 덮고 나머지 19개 구간이 사라집니다. 그래서 지배값을 숫자로 먼저 말하고 차트는 꼬리만 그립니다.`,
        },
        {
          label: '커버리지 기준을 넘긴 충전기만 센다.',
          text: '관측이 얇아 제외된 충전기 수는 위 커버리지 표시 옆에 있습니다.',
        },
        {
          label: '가동률은 관측된 시간 안에서의 비율이다.',
          text: '안 본 시간은 분모에 넣지 않습니다. 그래서 "가동률 100%" 는 "한 번도 고장난 적 없다" 가 아니라 "본 동안에는 고장이 없었다" 입니다.',
        },
      ]}

    >
      <div className="dstat">
        <div className="dstat__cell dstat__cell--good">
          <span className="dstat__value">{pct(s.total ? s.perfect / s.total : null, 1)}</span>
          <span className="dstat__label">
            관측 내내 UP · {int(s.perfect)}기
          </span>
        </div>
        <div className="dstat__cell dstat__cell--mid">
          <span className="dstat__value">{pct(s.total ? s.partial / s.total : null, 1)}</span>
          <span className="dstat__label">
            한 번이라도 DOWN · {int(s.partial)}기
          </span>
        </div>
        <div className="dstat__cell dstat__cell--bad">
          <span className="dstat__value">{pct(s.total ? s.dead / s.total : null, 1)}</span>
          <span className="dstat__label">
            관측 내내 DOWN · {int(s.dead)}기
          </span>
        </div>
      </div>
      <EChart
        option={option}
        height={CHART_H}
        ariaLabel={`가동률 분포. 관측 내내 UP ${int(s.perfect)}기, 한 번이라도 DOWN ${int(
          s.partial,
        )}기, 관측 내내 DOWN ${int(s.dead)}기. 구간별 개수는 표 보기에 있습니다.`}
      />
    </ChartFrame>
  )
}
